import React from "react";
import ReactDOM from "react-dom";
import _ from "lodash";
import { Map } from "immutable";

import BlockUpdateVO from "../../vo/BlockUpdate";
import UIActions from "../../actions/UIActions";
import DocumentStore from "../../document/DocumentStore";
import { COMMANDS } from "../../document/commands";
import EditorFrameContent from "./EditorFrameContent";
import { checkUpdateForCustomHtml } from "../../utils/HTMLUtils";
import { TEXT_FRAME_ID } from "../../constants/FrameConstants";
import TextToolBar from "./TextToolBar";
import EditorToolbar from "./EditorToolbar";
import TextEditorUtils from "../../utils/TextEditorUtils";
import HTMLEditorModal from "./HTMLEditorModal";
import MountFrame from "../MountFrame";

const focusTextFrame = (doc) => {
  doc.getElementById(TEXT_FRAME_ID).focus();
};
class TextEditor extends React.Component {
  static defaultProps = {
    mode: "text",
    contentPath: "text",
    popupTitle: "Edit HTML",
    enableToolbar: true,
    toolbar: null,
    includeSample: false,
  };

  constructor(props) {
    super(props);

    const modalActive = props.mode === "html";

    this.state = {
      selectionInfo: null,
      modalActive,
    };
  }

  _onEdit = (content, e) => {
    this._updateElementText(content);
  };

  _onEditComplete = () => {
    UIActions.clearSelectedElements();
  };

  _updateElementText = (/** Object * */ content) => {
    const { elementId, contentPath, includeSample, element } = this.props;
    const update = new BlockUpdateVO();

    if (element && element.get("type") === "button") {
      update.add(elementId, ["label"], content?.text ?? "");
    } else {
      update.add(elementId, ["content", contentPath], content?.text ?? "");
      if (includeSample) {
        update.add(elementId, ["content", "sample"], content?.sample ?? "");
      }
      checkUpdateForCustomHtml({ content, update, element, elementId });
    }

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  _onToolbarAction = (/** String * */ action, /** String * */ value) => {
    if (this.frame) {
      const doc = this._getFrameElement().contentDocument;
      const { selectionInfo } = this.state;

      switch (action) {
        case "bold":
          doc.execCommand("bold", false);
          focusTextFrame(doc);
          break;
        case "italic":
          doc.execCommand("italic", false);
          focusTextFrame(doc);
          break;
        case "ul":
          doc.execCommand("insertUnorderedList", false);
          focusTextFrame(doc);
          break;
        case "ol":
          doc.execCommand("insertOrderedList", false);
          focusTextFrame(doc);
          break;
        case "strikeThrough":
          doc.execCommand("strikeThrough", false);
          focusTextFrame(doc);
          break;
        case "createLink":
          // handle case where existing linking may be edited
          if (selectionInfo.collapsed && selectionInfo.anchorEl) {
            TextEditorUtils.selectElement(doc, selectionInfo.anchorEl);
          }

          doc.execCommand("createLink", false, value);
          break;
        case "clearFormat":
          doc.execCommand("removeFormat", false);
          break;
        case "unlink":
          if (selectionInfo.collapsed && selectionInfo.anchorEl) {
            TextEditorUtils.selectElement(doc, selectionInfo.anchorEl);
          }

          doc.execCommand("unlink", false);
          break;
        case "complete":
          UIActions.clearSelectedElements();
          break;
        default:
          break;
      }

      this._selectionUpdate();
    }
  };

  _getFrameElement = () => ReactDOM.findDOMNode(this.frame);

  _selectionUpdate = () => {
    const selection = this._getFrameElement().contentWindow.getSelection();
    if (selection && selection.rangeCount == 1) {
      const range = selection.getRangeAt(0);
      const rangeInfo = TextEditorUtils.getRangeInfo(range);
      this.setState({ selectionInfo: rangeInfo });
    }
  };

  _addSelectionListener = () => {
    const doc = this._getFrameElement()?.contentDocument;
    const mount = doc?.getElementById("render-mount");

    // need to wait for iframe to fully mount content before adding selection change
    if (doc && doc.readyState === "complete" && mount) {
      doc.addEventListener("selectionchange", this._selectionUpdate, false);
    } else {
      setTimeout(this._addSelectionListener, 0);
    }
  };

  _removeSelectionListener = () => {
    const doc = this._getFrameElement().contentDocument;
    if (doc) {
      doc.removeEventListener("selectionchange", this._selectionUpdate, false);
    }
  };

  componentDidMount() {
    if (this.props.mode === "text") {
      this._addSelectionListener();
    }
  }

  componentWillUnmount() {
    if (this.props.mode === "text") {
      this._removeSelectionListener();
    }
  }

  _getInlineEditor = (baseStyle) => {
    const {
      theme,
      layout,
      element,
      buttonElement,
      enableToolbar,
      designContext,
      toolbar,
    } = this.props;

    const elemProps = buttonElement
      ? buttonElement.get(layout)
      : element.get(layout);

    const elemHeight = elemProps.get("h");

    const frameStyle = {
      position: "absolute",
      height: elemHeight,
      width: "100%",
    };

    let updateToolbar = toolbar;
    if (enableToolbar && _.isEmpty(updateToolbar)) {
      updateToolbar = (
        <TextToolBar
          onAction={this._onToolbarAction}
          selectionInfo={this.state.selectionInfo}
          linkWarningEnabled={element.has("link")}
        />
      );
    }

    return (
      <div style={baseStyle}>
        {enableToolbar ? (
          <EditorToolbar position={elemHeight}>{updateToolbar}</EditorToolbar>
        ) : null}

        <MountFrame
          ref={(frame) => {
            this.frame = frame;
          }}
          style={frameStyle}
          useTemplateDoc={false}
        >
          <EditorFrameContent
            onEdit={this._onEdit}
            onEditComplete={this._onEditComplete}
            theme={theme}
            buttonElement={buttonElement}
            layout={layout}
            element={element}
            designContext={designContext}
          />
        </MountFrame>
      </div>
    );
  };

  _saveContent = (/** Object * */ content) => {
    this._updateElementText(content);
    this.setState({ modalActive: false });
  };

  _getPopupEditor = (baseStyle) => {
    const { element, contentPath, popupTitle, includeSample } = this.props;

    const content = element.get("content", Map());
    let text = content.get(contentPath);

    if (element && element.get("type") === "button") {
      text = element.get("label");
    }

    return (
      <div style={baseStyle}>
        <HTMLEditorModal
          linkWarningEnabled={element.has("link")}
          content={text}
          show={this.state.modalActive}
          includeSample={includeSample}
          title={`${popupTitle}`}
          onExited={this._onEditComplete}
          onSave={this._saveContent}
          onHide={this._closePopupEditor}
        />
      </div>
    );
  };

  _closePopupEditor = () => {
    this.setState({ modalActive: false });
  };

  render() {
    let view = null;
    const style = {
      width: "100%",
      height: "100%",
      position: "relative",
      backgroundColor: "rgba(0,0,0,0)",
      pointerEvents: "auto",
      outline: "dotted 1px black",
    };

    switch (this.props.mode) {
      case "html":
        view = this._getPopupEditor(style);
        break;
      case "text":
      default:
        view = this._getInlineEditor(style);
        break;
    }

    return view;
  }
}

export default TextEditor;
